import React, { useEffect, useState } from 'react';
import { Grid, TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem, Chip } from '@mui/material';
import { CheckCircle, XCircle, Search, Star, MapPin, ChevronRight, UserCheck, UserX, Eye } from 'lucide-react';
import { AdSpace, Order, Provider } from '../../types';
import { spacesAPI, ordersAPI, providersAPI } from '../../services/api';
import { PageHeader, Card, SectionTitle, StatusBadge } from '../../components/common/UIComponents';

const inputSx = {
  '& .MuiOutlinedInput-root':{ background:'#fff', borderRadius:'10px !important', fontSize:13, fontFamily:'DM Sans,sans-serif', '& fieldset':{ borderColor:'var(--border)' }, '&:hover fieldset':{ borderColor:'#d4c4b0' }, '&.Mui-focused fieldset':{ borderColor:'var(--brand)' } },
  '& .MuiInputLabel-root':{ fontSize:13, fontFamily:'DM Sans,sans-serif', color:'var(--text-muted)' },
  '& .MuiInputLabel-root.Mui-focused':{ color:'var(--brand)' },
};
const selectSx = { fontFamily:'DM Sans,sans-serif', fontSize:13, background:'#fff', borderRadius:'10px !important', '& .MuiOutlinedInput-notchedOutline':{ borderColor:'var(--border)' }, '&:hover .MuiOutlinedInput-notchedOutline':{ borderColor:'#d4c4b0' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline':{ borderColor:'var(--brand)' } };
const labelSx = { fontSize:13, fontFamily:'DM Sans,sans-serif', color:'var(--text-muted)', '&.Mui-focused':{ color:'var(--brand)' } };

// ── Admin Listings ─────────────────────────────────────────────────────────────
export const AdminListings: React.FC = () => {
  const [spaces, setSpaces] = useState<AdSpace[]>([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [approved, setApproved] = useState<string[]>([]);
  const [rejected, setRejected] = useState<string[]>([]);

  useEffect(() => { spacesAPI.getAll().then(setSpaces); }, []);

  const filtered = spaces.filter(s => {
    if (filter === 'approved' && !s.isApproved) return false;
    if (filter === 'pending' && s.isApproved) return false;
    if (search && !s.title.toLowerCase().includes(search.toLowerCase()) && !s.city.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const trafficColors: Record<string,{bg:string;color:string}> = {
    'High':{ bg:'var(--success-bg)', color:'var(--success)' },
    'Medium':{ bg:'var(--warning-bg)', color:'var(--warning)' },
    'Low':{ bg:'var(--bg-muted)', color:'var(--text-muted)' },
  };

  return (
    <div style={{ background:'var(--bg-base)', minHeight:'100vh', padding:'28px 32px' }} className="page-enter">
      <PageHeader title="Listing Management" subtitle="Review, approve and manage all ad space submissions"
        action={<div style={{ background:'var(--brand-light)', border:'1px solid var(--brand-mid)', borderRadius:10, padding:'6px 14px', fontSize:13, color:'var(--brand)', fontWeight:600 }}>{filtered.length} listings</div>}/>

      {/* Filters */}
      <div style={{ display:'flex', gap:12, marginBottom:24, flexWrap:'wrap', background:'#fff', padding:'16px 20px', borderRadius:14, border:'1px solid var(--border)', boxShadow:'var(--shadow-sm)' }}>
        <TextField size="small" placeholder="Search by title or city..." value={search} onChange={e=>setSearch(e.target.value)} sx={{ flex:1, minWidth:220, ...inputSx }}
          InputProps={{ startAdornment:<InputAdornment position="start"><Search size={14} color="var(--text-muted)"/></InputAdornment> }}/>
        <FormControl size="small" sx={{ minWidth:160 }}>
          <InputLabel sx={labelSx}>Status</InputLabel>
          <Select value={filter} onChange={e=>setFilter(e.target.value)} label="Status" sx={selectSx}>
            {[['all','All Listings'],['approved','Approved'],['pending','Pending Review']].map(([v,l])=>(
              <MenuItem key={v} value={v} sx={{ fontSize:13 }}>{l}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <Grid container spacing={2.5}>
        {filtered.map(space => {
          const isApproved = approved.includes(space.id) || space.isApproved;
          const isRejected = rejected.includes(space.id);
          const tc = trafficColors[space.trafficPriority];
          return (
            <Grid item xs={12} md={6} xl={4} key={space.id}>
              <div style={{ background:'#fff', border:`1px solid ${isRejected?'#f8c0c0':isApproved&&!rejected.includes(space.id)?'#b8e8ce':'var(--border)'}`, borderRadius:18, overflow:'hidden', boxShadow:'var(--shadow-sm)', transition:'all 0.2s' }}>
                <div style={{ position:'relative', height:168, overflow:'hidden' }}>
                  <img src={space.images[0]} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                  <div style={{ position:'absolute', inset:0, background:'linear-gradient(to top, rgba(0,0,0,0.4) 0%, transparent 55%)' }}/>
                  <div style={{ position:'absolute', top:10, right:10, display:'flex', gap:6 }}>
                    <span style={{ background:'rgba(255,255,255,0.92)', backdropFilter:'blur(4px)', color:'var(--text-primary)', padding:'3px 9px', borderRadius:20, fontSize:10, fontFamily:'DM Sans', fontWeight:600 }}>{space.mediaType}</span>
                  </div>
                  {space.isFeatured && (
                    <div style={{ position:'absolute', top:10, left:10, background:'var(--brand)', color:'#fff', padding:'3px 9px', borderRadius:20, fontSize:10, fontFamily:'DM Sans', fontWeight:700, display:'flex', alignItems:'center', gap:4 }}>
                      <Star size={8} fill="#fff"/> FEATURED
                    </div>
                  )}
                </div>
                <div style={{ padding:'15px 16px' }}>
                  <div style={{ fontWeight:600, fontSize:14, color:'var(--text-primary)', marginBottom:4, lineHeight:1.3 }}>{space.title}</div>
                  <div style={{ display:'flex', alignItems:'center', gap:4, color:'var(--text-muted)', fontSize:12, marginBottom:10 }}>
                    <MapPin size={11} color="var(--brand)"/> {space.city}, {space.state}
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                    <div style={{ display:'flex', gap:6 }}>
                      <span style={{ background:'var(--bg-muted)', color:'var(--text-secondary)', padding:'2px 8px', borderRadius:6, fontSize:11, fontFamily:'DM Sans' }}>{space.size}</span>
                      <span style={{ background:tc.bg, color:tc.color, padding:'2px 8px', borderRadius:6, fontSize:11, fontFamily:'DM Sans', fontWeight:600 }}>{space.trafficPriority}</span>
                    </div>
                    <span style={{ color:'var(--brand)', fontFamily:'Playfair Display,serif', fontWeight:700, fontSize:16 }}>₹{space.rentAmount.toLocaleString('en-IN')}</span>
                  </div>
                  {isRejected ? (
                    <div style={{ textAlign:'center', color:'var(--danger)', background:'var(--danger-bg)', borderRadius:9, padding:'9px', fontSize:13, fontWeight:600, border:'1px solid #f8c0c0' }}>✗ Rejected</div>
                  ) : isApproved ? (
                    <div style={{ textAlign:'center', color:'var(--success)', background:'var(--success-bg)', borderRadius:9, padding:'9px', fontSize:13, fontWeight:600, border:'1px solid #b8e8ce' }}>✓ Approved</div>
                  ) : (
                    <div style={{ display:'flex', gap:8 }}>
                      <button onClick={()=>{spacesAPI.approve(space.id);setApproved(p=>[...p,space.id]);}}
                        style={{ flex:1, background:'var(--success-bg)', border:'1px solid #b8e8ce', color:'var(--success)', borderRadius:9, padding:'9px', fontFamily:'DM Sans', fontSize:12, fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:5, transition:'all 0.18s' }}
                        onMouseEnter={e=>(e.currentTarget).style.background='#c8eedd'}
                        onMouseLeave={e=>(e.currentTarget).style.background='var(--success-bg)'}>
                        <CheckCircle size={13}/> Approve
                      </button>
                      <button onClick={()=>{spacesAPI.reject(space.id);setRejected(p=>[...p,space.id]);}}
                        style={{ flex:1, background:'var(--danger-bg)', border:'1px solid #f8c0c0', color:'var(--danger)', borderRadius:9, padding:'9px', fontFamily:'DM Sans', fontSize:12, fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:5, transition:'all 0.18s' }}
                        onMouseEnter={e=>(e.currentTarget).style.background='#fcd0d0'}
                        onMouseLeave={e=>(e.currentTarget).style.background='var(--danger-bg)'}>
                        <XCircle size={13}/> Reject
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </Grid>
          );
        })}
      </Grid>
    </div>
  );
};

// ── Admin Bookings ─────────────────────────────────────────────────────────────
export const AdminBookings: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [selected, setSelected] = useState<Order|null>(null);
  useEffect(()=>{ ordersAPI.getAll().then(setOrders); },[]);

  return (
    <div style={{ background:'var(--bg-base)', minHeight:'100vh', padding:'28px 32px' }} className="page-enter">
      <PageHeader title="Booking Management" subtitle="Review, approve and manage all campaign bookings"/>
      <Grid container spacing={3}>
        <Grid item xs={12} lg={selected?7:12}>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {orders.map(o => (
              <div key={o.id} onClick={()=>setSelected(o===selected?null:o)}
                style={{ background:'#fff', border:`1.5px solid ${selected?.id===o.id?'var(--brand)':'var(--border)'}`, borderRadius:14, padding:'16px 20px', cursor:'pointer', transition:'all 0.2s', boxShadow: selected?.id===o.id?'var(--shadow-brand)':'var(--shadow-sm)' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:10 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:5, flexWrap:'wrap' }}>
                      <span style={{ fontWeight:700, fontSize:14, color:'var(--text-primary)' }}>{o.campaignName}</span>
                      <span style={{ color:'var(--text-muted)', fontSize:12 }}>{o.id}</span>
                    </div>
                    <div style={{ display:'flex', gap:14, color:'var(--text-muted)', fontSize:12, flexWrap:'wrap' }}>
                      <span>{o.companyName}</span>
                      <span>{o.items.length} location{o.items.length>1?'s':''}</span>
                      <span style={{ color:'var(--brand)', fontWeight:600 }}>₹{o.grandTotal.toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:7, alignItems:'center' }}>
                    <span style={{ background:o.paymentStatus==='Received'?'var(--success-bg)':'var(--warning-bg)', color:o.paymentStatus==='Received'?'var(--success)':'var(--warning)', padding:'3px 9px', borderRadius:20, fontSize:11, fontWeight:600, fontFamily:'DM Sans' }}>{o.paymentStatus}</span>
                    <StatusBadge status={o.status} size="sm"/>
                    <ChevronRight size={15} color="var(--text-muted)"/>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Grid>

        {selected && (
          <Grid item xs={12} lg={5}>
            <Card style={{ position:'sticky', top:80 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                <SectionTitle>Order Details</SectionTitle>
                <button onClick={()=>setSelected(null)} style={{ background:'var(--bg-muted)', border:'1px solid var(--border)', borderRadius:8, width:28, height:28, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:'var(--text-muted)', fontSize:16 }}>×</button>
              </div>
              <div style={{ background:'var(--brand-light)', border:'1px solid var(--brand-mid)', borderRadius:12, padding:'12px 14px', marginBottom:16 }}>
                <div style={{ color:'var(--brand)', fontWeight:700, fontSize:15, marginBottom:3 }}>{selected.campaignName}</div>
                <div style={{ color:'var(--text-muted)', fontSize:12 }}>{selected.companyName} · {selected.id}</div>
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:16 }}>
                {selected.items.map((item,i) => (
                  <div key={i} style={{ display:'flex', gap:10, padding:'10px', background:'var(--bg-muted)', borderRadius:10, border:'1px solid var(--border-light)' }}>
                    <img src={item.space.images[0]} alt="" style={{ width:60, height:44, objectFit:'cover', borderRadius:7, flexShrink:0 }}/>
                    <div>
                      <div style={{ fontWeight:600, fontSize:13, color:'var(--text-primary)', marginBottom:2 }}>{item.space.title}</div>
                      <div style={{ color:'var(--text-muted)', fontSize:11 }}>{item.space.city} · ₹{item.space.rentAmount.toLocaleString('en-IN')}/mo</div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 0', borderTop:'1px solid var(--border)', marginBottom:16 }}>
                <span style={{ fontWeight:600, fontSize:14, color:'var(--text-primary)' }}>Grand Total</span>
                <span style={{ color:'var(--brand)', fontFamily:'Playfair Display,serif', fontWeight:700, fontSize:20 }}>₹{selected.grandTotal.toLocaleString('en-IN')}</span>
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <button style={{ flex:1, background:'var(--success-bg)', border:'1px solid #b8e8ce', color:'var(--success)', borderRadius:9, padding:'10px', fontFamily:'DM Sans', fontWeight:600, fontSize:13, cursor:'pointer' }}>✓ Approve</button>
                <button style={{ flex:1, background:'var(--info-bg)', border:'1px solid #c5d8f8', color:'var(--info)', borderRadius:9, padding:'10px', fontFamily:'DM Sans', fontWeight:600, fontSize:13, cursor:'pointer' }}>Assign Staff</button>
                <button style={{ flex:1, background:'var(--danger-bg)', border:'1px solid #f8c0c0', color:'var(--danger)', borderRadius:9, padding:'10px', fontFamily:'DM Sans', fontWeight:600, fontSize:13, cursor:'pointer' }}>✗ Reject</button>
              </div>
            </Card>
          </Grid>
        )}
      </Grid>
    </div>
  );
};

// ── Admin Providers ────────────────────────────────────────────────────────────
export const AdminProviders: React.FC = () => {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [approvedIds, setApprovedIds] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  useEffect(()=>{ providersAPI.getAll().then(setProviders); },[]);

  const filtered = providers.filter(p => !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.email.toLowerCase().includes(search.toLowerCase()));

  const kycStyle = (s:string) => ({
    'Approved':{ bg:'var(--success-bg)', color:'var(--success)', border:'#b8e8ce' },
    'Pending':{ bg:'var(--warning-bg)', color:'var(--warning)', border:'#fde8a8' },
    'Rejected':{ bg:'var(--danger-bg)', color:'var(--danger)', border:'#f8c0c0' },
  }[s] || { bg:'var(--bg-muted)', color:'var(--text-muted)', border:'var(--border)' });

  return (
    <div style={{ background:'var(--bg-base)', minHeight:'100vh', padding:'28px 32px' }} className="page-enter">
      <PageHeader title="Provider Management" subtitle="Manage advertising space providers and KYC approvals"
        action={<div style={{ display:'flex', gap:16 }}>
          {[{ l:'Active', v:providers.filter(p=>p.isActive).length, c:'var(--success)' }, { l:'Pending KYC', v:providers.filter(p=>p.kycStatus==='Pending').length, c:'var(--warning)' }].map(s=>(
            <div key={s.l} style={{ textAlign:'center' }}>
              <div style={{ fontFamily:'Playfair Display,serif', fontWeight:700, fontSize:22, color:s.c }}>{s.v}</div>
              <div style={{ fontSize:11, color:'var(--text-muted)', fontFamily:'DM Sans' }}>{s.l}</div>
            </div>
          ))}
        </div>}/>

      {/* Search */}
      <div style={{ background:'#fff', border:'1px solid var(--border)', borderRadius:14, padding:'14px 18px', marginBottom:20, boxShadow:'var(--shadow-sm)' }}>
        <TextField size="small" placeholder="Search providers..." value={search} onChange={e=>setSearch(e.target.value)} sx={{ width:300, ...inputSx }}
          InputProps={{ startAdornment:<InputAdornment position="start"><Search size={14} color="var(--text-muted)"/></InputAdornment> }}/>
      </div>

      <Card style={{ padding:0, overflow:'hidden' }}>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', minWidth:800 }}>
            <thead>
              <tr style={{ background:'var(--bg-muted)' }}>
                {['Provider','GST Number','KYC Status','Listings','Earnings','Status','Actions'].map(h=>(
                  <th key={h} style={{ color:'var(--text-muted)', fontFamily:'DM Sans', fontSize:11, fontWeight:600, textAlign:'left', padding:'12px 18px', textTransform:'uppercase', letterSpacing:'0.06em', whiteSpace:'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((p,i) => {
                const ks = kycStyle(approvedIds.includes(p.id)?'Approved':p.kycStatus);
                return (
                  <tr key={p.id} style={{ borderBottom:'1px solid var(--border-light)', transition:'background 0.15s' }}
                    onMouseEnter={e=>(e.currentTarget as HTMLTableRowElement).style.background='var(--bg-accent)'}
                    onMouseLeave={e=>(e.currentTarget as HTMLTableRowElement).style.background='#fff'}>
                    <td style={{ padding:'14px 18px' }}>
                      <div style={{ fontWeight:600, fontSize:13, color:'var(--text-primary)', marginBottom:2 }}>{p.name}</div>
                      <div style={{ fontSize:11, color:'var(--text-muted)' }}>{p.email}</div>
                    </td>
                    <td style={{ padding:'14px 18px', color:'var(--text-secondary)', fontSize:12, fontFamily:'monospace' }}>{p.gst}</td>
                    <td style={{ padding:'14px 18px' }}>
                      <span style={{ background:ks.bg, color:ks.color, border:`1px solid ${ks.border}`, padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:600, fontFamily:'DM Sans' }}>
                        {approvedIds.includes(p.id)?'Approved':p.kycStatus}
                      </span>
                    </td>
                    <td style={{ padding:'14px 18px', color:'var(--text-primary)', fontSize:13, fontWeight:600 }}>{p.totalListings}</td>
                    <td style={{ padding:'14px 18px', color:'var(--brand)', fontSize:13, fontWeight:700 }}>₹{p.totalEarnings.toLocaleString('en-IN')}</td>
                    <td style={{ padding:'14px 18px' }}>
                      <span style={{ background:p.isActive?'var(--success-bg)':'var(--bg-muted)', color:p.isActive?'var(--success)':'var(--text-muted)', border:`1px solid ${p.isActive?'#b8e8ce':'var(--border)'}`, padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:600, fontFamily:'DM Sans' }}>
                        {p.isActive?'Active':'Inactive'}
                      </span>
                    </td>
                    <td style={{ padding:'14px 18px' }}>
                      <div style={{ display:'flex', gap:6 }}>
                        {(p.kycStatus==='Pending' && !approvedIds.includes(p.id)) && (
                          <button onClick={()=>{providersAPI.approveKYC(p.id);setApprovedIds(prev=>[...prev,p.id]);}}
                            style={{ background:'var(--success-bg)', border:'1px solid #b8e8ce', color:'var(--success)', borderRadius:7, padding:'5px 11px', fontFamily:'DM Sans', fontSize:11, fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', gap:4 }}>
                            <UserCheck size={12}/> Approve
                          </button>
                        )}
                        <button style={{ background:'var(--bg-muted)', border:'1px solid var(--border)', color:'var(--text-secondary)', borderRadius:7, padding:'5px 11px', fontFamily:'DM Sans', fontSize:11, fontWeight:500, cursor:'pointer', display:'flex', alignItems:'center', gap:4 }}>
                          <Eye size={12}/> View
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};
